import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, Group, Stack } from '@mantine/core';
import { IconCancel as CancelIcon, IconDeviceFloppy as SaveIcon } from '@tabler/icons-react';
import { ReactNode } from 'react';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ZodSchema } from 'zod';

import { BaseCriteriaItemSchema } from '@/module/Evaluation/model/EvaluationSchema';
import { EvaluationCriteriaDiscriminator } from '@/module/Evaluation/model/EvaluationTypes';

import { Content, EnumSelectControl, TextInputControl } from '@/core/components';
import { FileInputControl } from '@/core/components/Layout/Form/components/FileInputControl';

interface EvaluationCriteriaItemFormProps<T extends BaseCriteriaItemSchema> {
  onChange: (request: T) => void;
  onCancel: () => void;

  defaultValues: DefaultValues<T>;

  isSubmitting: boolean;

  schema: ZodSchema<T>;

  discriminatorList: EvaluationCriteriaDiscriminator[];

  renderExtraFields: (isSubmitting: boolean) => ReactNode;
}

export function BaseEvaluationCriteriaItemForm<T extends BaseCriteriaItemSchema>(
  props: EvaluationCriteriaItemFormProps<T>,
) {
  const { onChange, defaultValues, isSubmitting, onCancel, schema, discriminatorList, renderExtraFields } = props;
  const { t } = useTranslation();

  const form = useForm<T>({
    defaultValues,
    resolver: zodResolver(schema),
  });

  const discriminatorOptions = discriminatorList.map(li => ({ label: li.name, value: li.code }));

  return (
    <Content type="full">
      <FormProvider {...form}>
        <Box
          flex={1}
          mb="10px"
          p="sm"
          component="form"
          onSubmit={form.handleSubmit(onChange)}
        >
          <Stack>
            {renderExtraFields(isSubmitting)}
            {discriminatorOptions.length > 0 && (
              <EnumSelectControl
                withAsterisk
                options={discriminatorOptions}
                name="discriminatorCode"
                label={t('evaluation.form.discriminator')}
                isSubmitting={isSubmitting}
              />
            )}
            <Group>
              <TextInputControl
                name="link"
                label={t('evaluation.form.link')}
                isSubmitting={isSubmitting}
              />
              <TextInputControl
                name="note"
                label={t('evaluation.form.note')}
                isSubmitting={isSubmitting}
              />
            </Group>
            <FileInputControl
              withAsterisk
              name="newFile"
              label={t('evaluation.form.file')}
              // @ts-expect-error todo check
              placeholder={defaultValues?.file?.title ?? t('evaluation.form.file')}
              description={t('evaluation.form.fileDescription')}
              isSubmitting={isSubmitting}
              accept="image/png,image/jpeg,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/zip,application/vnd.rar,application/x-rar-compressed,application/x-gzip,application/x-7z-compressed"
            />
          </Stack>
          <Group
            mt="lg"
            justify="flex-end"
          >
            {onCancel && (
              <Button
                size="sm"
                leftSection={<CancelIcon />}
                variant="outline"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                {t('button.cancel')}
              </Button>
            )}
            <Button
              size="sm"
              leftSection={<SaveIcon />}
              type="submit"
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              {t('button.save')}
            </Button>
          </Group>
        </Box>
      </FormProvider>
    </Content>
  );
}
